import { toast } from "react-toastify";
import NEW_API from "../../Api/NewApi";
import { uploadFile } from "../../Utils";

/**
 * Toutes les routes utilisees ici sont des routes custom : aucune route Strapi
 * par defaut n'est exposee pour les content-types ia_*. Les reponses sont des
 * objets plats, sans l'enveloppe { id, attributes } du core.
 */

/* -------------------------------------------------------------------------- */
/*                                   Tiroir                                   */
/* -------------------------------------------------------------------------- */

export const openIaDrawer = () => ({ type: "IA_OPEN_DRAWER" });
export const closeIaDrawer = () => ({ type: "IA_CLOSE_DRAWER" });
export const toggleIaDrawer = () => ({ type: "IA_TOGGLE_DRAWER" });
export const showIaHistory = (show) => ({ type: "IA_SHOW_HISTORY", payload: show });

/* -------------------------------------------------------------------------- */
/*                                 Discussions                                */
/* -------------------------------------------------------------------------- */

export const fetchIaDiscussions = () => async (dispatch) => {
  dispatch({ type: "IA_DISCUSSIONS_LOADING" });
  try {
    const { data } = await NEW_API.get("api/ia-discussions");
    dispatch({ type: "IA_DISCUSSIONS_SUCCESS", payload: data?.data || [] });
  } catch (error) {
    dispatch({ type: "IA_DISCUSSIONS_FAIL", payload: error?.message });
  }
};

/** Charge le fil complet d'une discussion. */
export const fetchIaDiscussion = (discussionId) => async (dispatch) => {
  dispatch({ type: "IA_MESSAGES_LOADING", payload: discussionId });
  try {
    const { data } = await NEW_API.get(`api/ia-discussion/${discussionId}/messages`);
    dispatch({
      type: "IA_MESSAGES_SUCCESS",
      payload: { discussionId, messages: data?.data || [] },
    });
  } catch (error) {
    dispatch({ type: "IA_MESSAGES_FAIL", payload: error?.message });
  }
};

export const newIaDiscussion = () => ({ type: "IA_NEW_DISCUSSION" });

export const deleteIaDiscussion = (discussionId, t) => async (dispatch) => {
  try {
    await NEW_API.delete(`api/ia-discussion/${discussionId}`);
    dispatch({ type: "IA_DISCUSSION_DELETED", payload: discussionId });
  } catch (error) {
    toast.error(t ? t("Deletion failed") : "Suppression impossible");
  }
};

/* -------------------------------------------------------------------------- */
/*                                   Images                                   */
/* -------------------------------------------------------------------------- */

const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/**
 * Televerse les images vers Strapi (aucun appel direct a OpenAI depuis le front)
 * et les met en attente d'envoi.
 */
export const addIaImages = (files, t) => async (dispatch, getState) => {
  const already = getState().iaReducer.pendingImages.length;
  const selected = Array.from(files || []);

  if (already + selected.length > MAX_IMAGES) {
    toast.error(
      t
        ? t("{{count}} images maximum per message", { count: MAX_IMAGES })
        : `${MAX_IMAGES} images maximum par message`
    );
    return;
  }

  for (const file of selected) {
    if (!ALLOWED_MIMES.includes(file.type)) {
      toast.error(t ? t("Unsupported image format") : "Format d'image non supporte");
      continue;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t ? t("Image too large (5 MB max)") : "Image trop volumineuse (5 Mo max)");
      continue;
    }

    const localId = `${file.name}-${file.size}-${Date.now()}`;
    dispatch({
      type: "IA_IMAGE_UPLOADING",
      payload: { localId, name: file.name, preview: URL.createObjectURL(file) },
    });

    try {
      const id = await uploadFile(file);
      dispatch({ type: "IA_IMAGE_UPLOADED", payload: { localId, id } });
    } catch (error) {
      dispatch({ type: "IA_IMAGE_FAILED", payload: { localId } });
      toast.error(t ? t("Image upload failed") : "Envoi de l'image impossible");
    }
  }
};

export const removeIaImage = (localId) => ({
  type: "IA_IMAGE_REMOVED",
  payload: localId,
});

/* -------------------------------------------------------------------------- */
/*                                  Question                                  */
/* -------------------------------------------------------------------------- */

/**
 * Envoie la question au back. Le back interroge OpenAI et renvoie la reponse
 * token par token via socket.io : cette action ne recoit que les identifiants.
 */
export const askIa = (question, locale, t) => async (dispatch, getState) => {
  const state = getState().iaReducer;
  const imageIds = state.pendingImages.filter((i) => i.id).map((i) => i.id);

  dispatch({ type: "IA_ASK_LOADING" });

  try {
    const { data } = await NEW_API.post("api/ia-ask", {
      data: {
        discussionId: state.currentDiscussionId || undefined,
        question,
        imageIds,
        locale,
      },
    });

    const { discussionId, userMessageId, messageId } = data?.data || {};

    dispatch({
      type: "IA_ASK_SUCCESS",
      payload: {
        discussionId,
        userMessageId,
        messageId,
        question,
        images: state.pendingImages.filter((i) => i.id),
      },
    });

    // La liste d'historique doit refleter la nouvelle discussion immediatement.
    if (!state.currentDiscussionId) dispatch(fetchIaDiscussions());

    return discussionId;
  } catch (error) {
    const status = error?.response?.status;
    const message =
      status === 429
        ? t
          ? t("Daily question limit reached")
          : "Limite de questions quotidienne atteinte"
        : status === 503
        ? t
          ? t("The AI assistant is not configured")
          : "L'assistant IA n'est pas configure"
        : t
        ? t("Sending failed")
        : "Envoi impossible";

    dispatch({ type: "IA_ASK_FAIL", payload: message });
    toast.error(message);
    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                          Evenements de streaming                           */
/* -------------------------------------------------------------------------- */

export const iaTokenReceived = ({ messageId, delta }) => ({
  type: "IA_TOKEN",
  payload: { messageId, delta },
});

/**
 * Aucune action de sources : le back ne transmet pas les documents utilises au
 * navigateur, leurs noms de fichiers etant internes.
 */
export const iaAnswerDone = ({ messageId, content, costUsd }) => ({
  type: "IA_DONE",
  payload: { messageId, content, costUsd },
});

export const iaAnswerError = ({ messageId, message }) => ({
  type: "IA_ERROR",
  payload: { messageId, message },
});
